// Forecast encompassing (Fair & Shiller 1990; Forrest, Goddard & Simmons 2005).
//
// "Model log loss vs market log loss" is the wrong question. Almost nothing
// beats a liquid closing line outright, so a model that loses that comparison
// has been told only that it is like everything else. The question worth asking
// is conditional: GIVEN the market's price, does the model carry any
// information the market has not already priced?
//
// One logistic regression answers it:
//
//     logit P(y = 1) = b0 + b1 · logit(p_market) + b2 · logit(p_model)
//
// b2 is the whole test.
//
//   |t| < 1.96 — the market already knows what the model knows. This is the
//   usual answer, and it is a stopping condition: there is no tuning of a
//   model whose information the market has already absorbed.
//
//   t >= 1.96 — the model adds something. What to bet is then the BLEND this
//   regression fits, never the raw model: b1 is rarely zero, so the extra
//   information is a correction to the market's price, not a replacement for
//   it.
//
//   t <= -1.96 — the model is reliably wrong where the market is not. That is
//   not an edge upside down, it is a bug: a flipped sign, or a result leaking
//   into a feature. Find it before believing any other number.
//
// THE BLEND'S LOG LOSS IS IN-SAMPLE. The coefficients were fitted on the same
// matches they are scored over, so the improvement flatters itself by
// construction — it is the ceiling on what the information is worth, not a
// promise. Only a held-out period says whether it survives, which is why the
// verdict says so out loud.
//
// Deterministic: no clock, no randomness, same triples in, same numbers out.

import { logLoss } from './metrics.js';
import { NO_VALUE } from '../format.js';

/**
 * Below this many scored matches the regression is fitting noise.
 *
 * Exported because `engine/proof.js` writes down what "proven" means and must
 * read this number rather than restate it: a bar typed twice is a bar that
 * moves when nobody is looking.
 */
export const MIN_MATCHES = 100;

/** Probabilities are clipped before the logit, so a 0 or a 1 is not an infinity. */
const P_CLIP = 1e-6;

/** And the linear predictor after it — beyond this, exp() is 0 or overflow. */
const ETA_CLIP = 35;

const MAX_ITER = 50;
const TOL = 1e-10;

/** The IRLS weight floor; a saturated row must not empty a row of the Hessian. */
const MIN_WEIGHT = 1e-10;

/** Two-sided 5%, the threshold the whole verdict turns on. */
export const T_CRITICAL = 1.96;

// --- the normal distribution, without a dependency --------------------------------

// Abramowitz & Stegun 7.1.26: |error| < 1.5e-7. A p-value is read to three
// decimals and acted on at one threshold, so that is four digits spare.
const ERF_C = [0.254829592, -0.284496736, 1.421413741, -1.453152027, 1.061405429];
const ERF_P = 0.3275911;

function erf(x) {
  const sign = x < 0 ? -1 : 1;
  const z = Math.abs(x);
  const t = 1 / (1 + ERF_P * z);
  const poly = ((((ERF_C[4] * t + ERF_C[3]) * t + ERF_C[2]) * t + ERF_C[1]) * t + ERF_C[0]) * t;
  return sign * (1 - poly * Math.exp(-z * z));
}

/** Φ, the standard normal CDF. Φ(0) = 0.5, Φ(1.96) ≈ 0.975. */
export const normalCdf = (x) => 0.5 * (1 + erf(x / Math.SQRT2));

/**
 * Two-sided p-value for a z statistic, floored at 0 — the erf approximation
 * can overshoot 1 by a ten-millionth, and a negative p-value is not a number
 * anyone should be shown.
 */
export const twoSidedP = (t) => (Number.isFinite(t) ? Math.max(0, 2 * (1 - normalCdf(Math.abs(t)))) : null);

// --- the maths --------------------------------------------------------------------

const sigmoid = (eta) => 1 / (1 + Math.exp(-eta));

const clampEta = (eta) => Math.min(Math.max(eta, -ETA_CLIP), ETA_CLIP);

const logit = (p) => {
  const c = Math.min(Math.max(p, P_CLIP), 1 - P_CLIP);
  return Math.log(c / (1 - c));
};

const linear = (x, beta) => clampEta(x[0] * beta[0] + x[1] * beta[1] + x[2] * beta[2]);

/**
 * Invert a 3x3 by Gauss-Jordan with partial pivoting, or null when it cannot
 * be done. Null is a real answer here rather than an error: a strategy whose
 * "model" IS the market gives two identical columns, and the honest reading of
 * a singular design is that the test has nothing to separate.
 */
function invert3(m) {
  const scale = Math.max(...m.flat().map(Math.abs));
  if (!(scale > 0)) return null;

  const a = m.map((row, i) => [...row, ...[0, 1, 2].map((j) => (i === j ? 1 : 0))]);
  for (let col = 0; col < 3; col++) {
    let pivot = col;
    for (let r = col + 1; r < 3; r++) {
      if (Math.abs(a[r][col]) > Math.abs(a[pivot][col])) pivot = r;
    }
    if (!(Math.abs(a[pivot][col]) > 1e-12 * scale)) return null;
    [a[col], a[pivot]] = [a[pivot], a[col]];

    const d = a[col][col];
    for (let j = 0; j < 6; j++) a[col][j] /= d;
    for (let r = 0; r < 3; r++) {
      if (r === col) continue;
      const f = a[r][col];
      if (f === 0) continue;
      for (let j = 0; j < 6; j++) a[r][j] -= f * a[col][j];
    }
  }

  const inv = a.map((row) => row.slice(3));
  return inv.flat().every(Number.isFinite) ? inv : null;
}

/** Gradient and observed information of the log likelihood at `beta`. */
function score(rows, beta) {
  const gradient = [0, 0, 0];
  const hessian = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  for (const { x, y } of rows) {
    const mu = sigmoid(linear(x, beta));
    const w = Math.max(mu * (1 - mu), MIN_WEIGHT);
    const residual = y - mu;
    for (let i = 0; i < 3; i++) {
      gradient[i] += residual * x[i];
      for (let j = 0; j < 3; j++) hessian[i][j] += w * x[i] * x[j];
    }
  }
  return { gradient, hessian };
}

/**
 * Newton-Raphson / IRLS on the three-column design. Returns the coefficients
 * and the covariance — the inverse observed information AT THE ANSWER, not at
 * the last place the search stood before it got there.
 */
function fitLogistic(rows) {
  let beta = [0, 0, 0];
  for (let iter = 0; iter < MAX_ITER; iter++) {
    const { gradient, hessian } = score(rows, beta);
    const inv = invert3(hessian);
    if (!inv) return null;
    const step = inv.map((row) => row[0] * gradient[0] + row[1] * gradient[1] + row[2] * gradient[2]);
    if (!step.every(Number.isFinite)) return null;
    beta = beta.map((b, i) => b + step[i]);
    if (!beta.every(Number.isFinite)) return null;
    if (step.every((d) => Math.abs(d) < TOL)) break;
  }
  const cov = invert3(score(rows, beta).hessian);
  return cov ? { beta, cov } : null;
}

// --- the test ---------------------------------------------------------------------

const NO_COEFFICIENTS = { intercept: null, market: null, model: null };

/** The shape with no fit in it: too few rows, or a design with nothing to separate. */
const untested = (n, verdict, market, model) => ({
  n,
  coefficients: { ...NO_COEFFICIENTS },
  standardErrors: { ...NO_COEFFICIENTS },
  tStats: { ...NO_COEFFICIENTS },
  pValues: { ...NO_COEFFICIENTS },
  logLoss: { market, model, blend: null },
  verdict,
});

/**
 * The encompassing test over `[{ pMarket, pModel, y }]` — one row per scored
 * match, the same match and the same outcome on both sides. A row missing
 * either probability, or without a 0/1 outcome, is dropped rather than guessed.
 */
export function encompassingTest(triples) {
  const rows = [];
  for (const triple of triples ?? []) {
    const { pMarket, pModel, y } = triple ?? {};
    if (!Number.isFinite(pMarket) || !Number.isFinite(pModel)) continue;
    if (y !== 0 && y !== 1) continue;
    rows.push({ x: [1, logit(pMarket), logit(pModel)], y, pMarket, pModel });
  }

  const n = rows.length;
  const marketLoss = n ? logLoss(rows.map((r) => ({ p: r.pMarket, y: r.y }))) : null;
  const modelLoss = n ? logLoss(rows.map((r) => ({ p: r.pModel, y: r.y }))) : null;

  if (n < MIN_MATCHES) return untested(n, 'too-few', marketLoss, modelLoss);

  const fit = fitLogistic(rows);
  // A design the fit cannot separate says the model is the market wearing a
  // hat, which is exactly "no information beyond the close".
  if (!fit) return untested(n, 'no-information', marketLoss, modelLoss);

  const { beta, cov } = fit;
  const se = [0, 1, 2].map((i) => (cov[i][i] > 0 ? Math.sqrt(cov[i][i]) : null));
  const t = beta.map((b, i) => (se[i] === null ? null : b / se[i]));
  const blendLoss = logLoss(rows.map((r) => ({ p: sigmoid(linear(r.x, beta)), y: r.y })));

  const tModel = t[2];
  const verdict = !Number.isFinite(tModel)
    ? 'no-information'
    : tModel >= T_CRITICAL
      ? 'signal'
      : tModel <= -T_CRITICAL
        ? 'negative'
        : 'no-information';

  const triple = (values) => ({ intercept: values[0], market: values[1], model: values[2] });
  return {
    n,
    coefficients: triple(beta),
    standardErrors: triple(se),
    tStats: triple(t),
    pValues: triple(t.map(twoSidedP)),
    logLoss: { market: marketLoss, model: modelLoss, blend: blendLoss },
    verdict,
  };
}

// --- the fit a live prediction is allowed to use --------------------------------------
//
// THE TEST ABOVE IS A VERDICT; THIS IS THE THING THE VERDICT LETS YOU BET
// (engine review #12). `runStrategy` used to set `pModel = predict()` — the raw
// model, replacing the market outright — while this file sat in the backtest
// summary saying "bet the blend, never the raw model" and being read by nobody.
//
// So the three coefficients ride WITH the model's state, and the strategy
// predicts what the regression actually fitted:
//
//     pModel = sigmoid(b0 + b1 · logit(pMarket) + b2 · logit(pRaw))
//
// b1 is rarely zero and is usually close to one: the market is the prior, and
// the model is a small correction to it with a weight somebody measured. Below
// the t threshold there is no correction to make and nothing is printed at all,
// which is the honest reading of "the market already knows what this model
// knows".

/** Models whose probability IS the market already. There is nothing to correct. */
const MARKET_MODELS = ['none', 'market-consensus', 'sharp-anchor'];

/**
 * Does this model make a claim the market has to be asked about?
 *
 * `none` is the book's own price devigged, `market-consensus` is the board, and
 * `sharp-anchor` is one sharp book — all three ARE market prices, so blending
 * them into a market price would be regressing a number on itself.
 */
export const correctsMarket = (modelId) => !!modelId && !MARKET_MODELS.includes(modelId);

/** How a fit is filed: one model, one sport, because a fit is about both. */
export const fitKey = (modelId, sport) => `${modelId}:${sport}`;

/**
 * The five numbers a live prediction needs, off the back of a full test, or
 * null where the test had nothing to say. `fittedAt` is when the sample ended
 * and `fittedFor` is the model state it was measured on — a state that has
 * refitted since has not been tested, and a fit that outlived its model is the
 * one way this could quietly claim more than it measured.
 */
export function fitFrom(result, { fittedAt = null, fittedFor = null } = {}) {
  const b = result?.coefficients;
  const t2 = result?.tStats?.model ?? null;
  if (!b || !Number.isFinite(b.intercept) || !Number.isFinite(b.market) || !Number.isFinite(b.model)) return null;
  if (!Number.isFinite(t2)) return null;
  return {
    b0: b.intercept,
    b1: b.market,
    b2: b.model,
    t2,
    n: result.n ?? null,
    fittedAt,
    ...(fittedFor ? { fittedFor } : {}),
  };
}

/** The fit over a sample of `{ pMarket, pModel, y }`, or null. */
export const fitEncompassing = (triples, opts) => fitFrom(encompassingTest(triples), opts);

/**
 * May this fit be bet on? Only above the same t the verdict turns on — a
 * coefficient the data cannot separate from zero is a weight nobody measured.
 */
export const fitProven = (fit) =>
  !!fit && Number.isFinite(fit.b2) && Number.isFinite(fit.t2) && fit.t2 >= T_CRITICAL;

/**
 * The market's price corrected by the model, on the fit's own terms, or null
 * when either forecast is missing or the fit is not one to bet on.
 */
export function correctedProbability(fit, pMarket, pRaw) {
  if (!fitProven(fit)) return null;
  if (!Number.isFinite(pMarket) || !Number.isFinite(pRaw)) return null;
  if (pMarket <= 0 || pMarket >= 1 || pRaw <= 0 || pRaw >= 1) return null;
  const p = sigmoid(clampEta(fit.b0 + fit.b1 * logit(pMarket) + fit.b2 * logit(pRaw)));
  return Number.isFinite(p) && p > 0 && p < 1 ? p : null;
}

// --- saying it out loud -------------------------------------------------------------

/** A t-statistic the way a sentence prints one, with a real minus (U+2212). */
const tText = (t) => (Number.isFinite(t) ? t.toFixed(1).replace('-', '−') : NO_VALUE);

/** A log loss to the three decimals that separate two forecasters. */
const lossText = (x) => (Number.isFinite(x) ? x.toFixed(3) : NO_VALUE);

/** What the test found, in words, for anyone who does not read t-statistics. */
export function verdictText(result) {
  const t = result?.tStats?.model ?? null;
  switch (result?.verdict) {
    case 'no-information': {
      const stat = Number.isFinite(t) ? ` (t = ${tText(t)})` : '';
      return `The market already knows what this model knows${stat}. Tuning will not change that.`;
    }
    case 'negative':
      return `The model is systematically wrong where the market is not (t = ${tText(t)}). Check for a sign error or a leak before believing anything.`;
    case 'signal':
      return `Adds information beyond the close (t = ${tText(t)}): blended, log loss ${
        lossText(result.logLoss?.market)} → ${lossText(result.logLoss?.blend)}. Bet the blend, never the raw model, and confirm on a held-out period first.`;
    default:
      return 'Too few matches to test.';
  }
}
